import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getApiBase } from './apiBase';

const API = getApiBase();

/* ─── Design Tokens ──────────────────────────────────────────────────────── */
const C = {
  primary:           '#9f4200',
  primaryContainer:  '#f27121',
  secondary:         '#4c56af',
  secondaryContainer:'#959efd',
  tertiary:          '#835500',
  tertiaryContainer: '#ca8a22',
  surface:           '#fbf9f4',
  surfaceContainerLow: '#f5f3ee',
  surfaceContainerLowest: '#ffffff',
  onSurface:         '#1b1c19',
  onSurfaceVariant:  '#584237',
  outlineVariant:    '#dfc0b2',
  onPrimary:         '#ffffff',
  onSecondary:       '#ffffff',
  navy:              '#0f172a',
};

/* ─── Hooks ──────────────────────────────────────────────────────────────── */
function useCountUp(target, duration = 2000, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) { setVal(0); return; }
    let s = null;
    const step = (ts) => {
      if (!s) s = ts;
      const p = Math.min((ts - s) / duration, 1);
      setVal(Math.floor((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return val;
}

function useInView(ref, threshold = 0.15) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return inView;
}

/* ─── Primitives ─────────────────────────────────────────────────────────── */
function Icon({ name, size = 24, style = {} }) {
  return (
    <span className="material-symbols-outlined" style={{ fontSize: size, lineHeight: 1, userSelect: 'none', ...style }}>
      {name}
    </span>
  );
}

function Typewriter({ texts, speed = 75, pause = 2400 }) {
  const [display, setDisplay] = useState('');
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('typing');
  const [charIdx, setCharIdx] = useState(0);
  useEffect(() => {
    let t;
    if (phase === 'typing') {
      if (charIdx < texts[idx].length) t = setTimeout(() => { setDisplay(texts[idx].slice(0, charIdx + 1)); setCharIdx(c => c + 1); }, speed);
      else t = setTimeout(() => setPhase('pausing'), pause);
    } else if (phase === 'pausing') {
      t = setTimeout(() => setPhase('erasing'), 400);
    } else {
      if (charIdx > 0) t = setTimeout(() => { setDisplay(texts[idx].slice(0, charIdx - 1)); setCharIdx(c => c - 1); }, speed / 2);
      else { setIdx(i => (i + 1) % texts.length); setPhase('typing'); }
    }
    return () => clearTimeout(t);
  }, [phase, charIdx, idx, texts, speed, pause]);
  return (
    <span style={{ borderRight: `2px solid ${C.primaryContainer}`, paddingRight: 2, animation: 'cursorBlink 0.9s step-end infinite' }}>
      {display}
    </span>
  );
}

function StatCounter({ value, suffix = '', label, inView }) {
  const num = useCountUp(value, 1800, inView);
  return (
    <div style={{ textAlign: 'center' }}>
      <div className="lp-stat-num" style={{ fontSize: 44, fontWeight: 800, color: '#ffffff', fontFamily: 'Lexend, sans-serif', lineHeight: 1, letterSpacing: '-0.03em' }}>
        {num}{suffix}
      </div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 8, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>{label}</div>
    </div>
  );
}

/* ─── Data ───────────────────────────────────────────────────────────────── */
const VALUES = [
  { icon: 'emoji_events', color: C.tertiaryContainer, label: 'Excellence',        desc: '100% annual results with consistent academic achievement.' },
  { icon: 'diversity_3',  color: C.secondary,         label: 'Inclusivity',       desc: 'A welcoming environment for every child to thrive.' },
  { icon: 'psychology',   color: C.primary,            label: 'Critical Thinking', desc: 'Encouraging curiosity, reasoning, and problem-solving.' },
  { icon: 'favorite',     color: '#c0392b',            label: 'Care & Values',     desc: 'Nurturing character, discipline, and moral values.' },
];

const FACILITIES = [
  { icon: 'computer',       label: 'Computer Lab',    desc: 'Modern PCs, high-speed internet, and age-appropriate software for digital literacy.',    tags: ['Modern PCs', 'Internet', 'Coding'] },
  { icon: 'videocam',       label: 'Smart Classroom', desc: 'HD projector and audio system for interactive, visual learning experiences.',            tags: ['HD Projector', 'Smart Board', 'Audio'] },
  { icon: 'directions_bus', label: 'Transport',       desc: 'Safe school bus service covering Ishapur, Laxminagar, and surrounding areas.',          tags: ['School Bus', 'Safe Routes', 'Timely'] },
  { icon: 'sports_soccer',  label: 'Sports Ground',   desc: 'Spacious playground for cricket, football, kabaddi, and athletics.',                    tags: ['Cricket', 'Football', 'Kabaddi'] },
];

const NAV = ['About', 'Academics', 'Facilities', 'Notices', 'Contact'];

/* ─── Facility Card (with hover overlay) ────────────────────────────────── */
function FacilityCard({ facility: f, index: i }) {
  const [hovered, setHovered] = useState(false);
  const iconColors = [C.secondary, C.tertiary, C.tertiaryContainer, C.primary];
  const iconBgs    = [
    `rgba(76,86,175,0.1)`,
    `rgba(131,85,0,0.1)`,
    `rgba(202,138,34,0.1)`,
    `rgba(159,66,0,0.1)`,
  ];
  const color = iconColors[i % iconColors.length];
  const bg    = iconBgs[i % iconBgs.length];

  return (
    <div
      className={`lp-card-lift d${i + 1}`}
      data-reveal="scale"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ borderRadius: '1rem 0.25rem 1rem 0.25rem', overflow: 'hidden', boxShadow: '0 2px 24px rgba(27,28,25,0.06)', border: `1px solid rgba(223,192,178,0.3)`, background: C.surfaceContainerLowest, position: 'relative' }}
    >
      {/* Icon header */}
      <div style={{ background: bg, padding: '28px 24px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 52, height: 52, borderRadius: '0.75rem 0.25rem 0.75rem 0.25rem', background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name={f.icon} size={26} style={{ color: '#fff' }} />
        </div>
        <h3 style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 16, color: C.onSurface, margin: 0 }}>{f.label}</h3>
      </div>
      {/* Body */}
      <div style={{ padding: '20px 24px 24px' }}>
        <p style={{ fontSize: 13, color: C.onSurfaceVariant, lineHeight: 1.7, margin: '0 0 16px', fontFamily: 'Inter, sans-serif' }}>{f.desc}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {f.tags.map(t => (
            <span key={t} style={{ padding: '3px 10px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: bg, color: color, letterSpacing: '0.04em', fontFamily: 'Inter, sans-serif' }}>{t}</span>
          ))}
        </div>
      </div>
      {/* Hover overlay */}
      <div style={{ position: 'absolute', inset: 0, background: color, opacity: hovered ? 0.06 : 0, transition: 'opacity 0.3s', pointerEvents: 'none', borderRadius: 'inherit' }} />
    </div>
  );
}

/* ─── Splash Screen ──────────────────────────────────────────────────────── */
function SplashScreen({ onComplete }) {
  const [fading, setFading] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const t1 = setTimeout(() => setFading(true), 1800);
    const t2 = setTimeout(() => onComplete(), 2400);
    return () => { 
      document.body.style.overflow = ''; 
      clearTimeout(t1); 
      clearTimeout(t2); 
    };
  }, [onComplete]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#000000', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      opacity: fading ? 0 : 1, transition: 'opacity 0.6s ease-in-out',
    }}>
      <div style={{ animation: 'splashPulse 2s ease-in-out infinite' }}>
        <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="LKPS" 
             style={{ width: 120, height: 120, objectFit: 'contain', marginBottom: 24 }} 
             onError={e => { e.target.style.display = 'none'; }} />
      </div>
      <div style={{ animation: 'fadeUp 0.8s ease-out' }}>
        <h2 style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300, fontSize: 40, color: '#ffffff', margin: 0, letterSpacing: '0.06em', textAlign: 'center', padding: '0 20px', textTransform: 'uppercase' }}>
          Lord Krishna Public School
        </h2>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function LandingPage() {
  const [showSplash, setShowSplash]     = useState(true);
  const [menuOpen, setMenuOpen]         = useState(false);
  const [scrolled, setScrolled]         = useState(false);
  const [regOpen, setRegOpen]           = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [regForm, setRegForm]           = useState({ name: '', phone: '', email: '' });
  const [regError, setRegError]         = useState('');
  const [regDone, setRegDone]           = useState(false);
  const [regLoading, setRegLoading]     = useState(false);
  const [notices, setNotices]           = useState([]);
  const [mouse, setMouse]               = useState({ x: 0, y: 0 });

  const statsRef    = useRef(null);
  const heroRef     = useRef(null);
  const statsInView = useInView(statsRef, 0.2);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const obs = new IntersectionObserver(([e]) => setScrolled(!e.isIntersecting), { threshold: 0 });
    obs.observe(hero);
    return () => obs.disconnect();
  }, []);

  useEffect(() => { const t = setTimeout(() => setBrochureOpen(true), 5000); return () => clearTimeout(t); }, []);

  useEffect(() => {
    fetch(`${API}/notices/public`).then(r => r.json()).then(d => { if (Array.isArray(d) && d.length) setNotices(d); }).catch(() => {});
  }, []);

  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('revealed'); e.target.classList.remove('reveal-out'); }
        else { e.target.classList.remove('revealed'); e.target.classList.add('reveal-out'); }
      });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const onMouseMove = useCallback((e) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMouse({ x: ((e.clientX - rect.left) / rect.width - 0.5) * 2, y: ((e.clientY - rect.top) / rect.height - 0.5) * 2 });
  }, []);

  const scrollTo = id => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

  const handleReg = async (e) => {
    e.preventDefault(); setRegError('');
    if (!regForm.name.trim()) return setRegError('Student name is required.');
    if (!/^\d{10}$/.test(regForm.phone)) return setRegError('Enter a valid 10-digit phone number.');
    setRegLoading(true);
    try {
      const res = await fetch(`${API}/admissions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(regForm) });
      if (!res.ok) throw new Error();
      setRegDone(true);
    } catch { setRegError('Something went wrong. Please try again.'); }
    finally { setRegLoading(false); }
  };

  const closeReg = () => { setRegOpen(false); setRegDone(false); setRegForm({ name: '', phone: '', email: '' }); setRegError(''); };

  const tagClass = (cat) => {
    const map = { event: 'tag-event', important: 'tag-important', holiday: 'tag-holiday', admission: 'tag-admission' };
    return `lp-tag ${map[cat] || 'tag-general'}`;
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', color: C.onSurface, overflowX: 'hidden', background: C.surface }}>
      {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}

      {/* ── Global Styles ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700;800;900&family=Inter:wght@300;400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200');

        @keyframes cursorBlink  { 0%,100%{border-color:${C.primaryContainer}} 50%{border-color:transparent} }
        @keyframes orbFloat1    { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(50px,-35px) scale(1.06)} 66%{transform:translate(-25px,45px) scale(0.94)} }
        @keyframes orbFloat2    { 0%,100%{transform:translate(0,0) scale(1)} 40%{transform:translate(-55px,28px) scale(1.08)} 70%{transform:translate(35px,-55px) scale(0.92)} }
        @keyframes orbFloat3    { 0%,100%{transform:translate(0,0)} 50%{transform:translate(25px,-25px)} }
        @keyframes fadeUp       { from{opacity:0;transform:translateY(36px)} to{opacity:1;transform:none} }
        @keyframes floatY       { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes gradShift    { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
        @keyframes shimmer      { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes splashPulse  { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.04); } }

        [data-reveal]{opacity:0;will-change:opacity,transform;transition:opacity 0.7s cubic-bezier(0.16,1,0.3,1),transform 0.7s cubic-bezier(0.16,1,0.3,1);}
        [data-reveal="up"]   {transform:translateY(44px);}
        [data-reveal="left"] {transform:translateX(44px);}
        [data-reveal="right"]{transform:translateX(-44px);}
        [data-reveal="scale"]{transform:scale(0.86);}
        [data-reveal].revealed{opacity:1!important;transform:none!important;}
        [data-reveal].reveal-out{opacity:0;}
        [data-reveal="up"].reveal-out   {transform:translateY(44px);}
        [data-reveal="left"].reveal-out {transform:translateX(44px);}
        [data-reveal="right"].reveal-out{transform:translateX(-44px);}
        [data-reveal="scale"].reveal-out{transform:scale(0.86);}
        .d1{transition-delay:0.07s!important}.d2{transition-delay:0.14s!important}
        .d3{transition-delay:0.21s!important}.d4{transition-delay:0.28s!important}

        .lp-nav-link{position:relative;text-decoration:none;font-size:13px;font-weight:600;padding:4px 0;transition:color 0.2s;background:none;border:none;cursor:pointer;font-family:'Inter',sans-serif;color:${C.onSurfaceVariant};}
        .lp-nav-link::after{content:'';position:absolute;bottom:-3px;left:0;width:0;height:2px;background:${C.primary};border-radius:2px;transition:width 0.28s ease;}
        .lp-nav-link:hover{color:${C.primary};}
        .lp-nav-link:hover::after{width:100%;}

        .lp-btn-primary{display:inline-flex;align-items:center;gap:8px;padding:13px 24px;border-radius:12px;font-size:14px;font-weight:700;cursor:pointer;border:none;font-family:'Inter',sans-serif;background:${C.primary};color:${C.onPrimary};transition:background 0.25s,transform 0.25s,box-shadow 0.25s;box-shadow:0 4px 20px rgba(159,66,0,0.25);min-height:48px;touch-action:manipulation;}
        .lp-btn-primary:hover{background:${C.primaryContainer};transform:translateY(-2px);box-shadow:0 8px 28px rgba(242,113,33,0.35);}
        .lp-btn-secondary{display:inline-flex;align-items:center;gap:8px;padding:12px 22px;border-radius:12px;font-size:14px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;background:${C.surfaceContainerLowest};color:${C.secondary};border:1px solid rgba(223,192,178,0.3);transition:background 0.25s,transform 0.25s,box-shadow 0.25s;box-shadow:0 2px 8px rgba(27,28,25,0.06);min-height:48px;touch-action:manipulation;}
        .lp-btn-secondary:hover{background:${C.surfaceContainerLow};transform:translateY(-2px);box-shadow:0 6px 20px rgba(27,28,25,0.1);}
        .lp-btn-nav-reg{background:${C.primary};color:#fff;border:none;padding:9px 18px;border-radius:10px;font-size:13px;font-weight:700;cursor:pointer;transition:background 0.2s,transform 0.2s;font-family:'Inter',sans-serif;min-height:40px;touch-action:manipulation;display:inline-flex;align-items:center;line-height:1;}
        .lp-btn-nav-reg:hover{background:${C.primaryContainer};transform:translateY(-1px);}

        .lp-card-lift{transition:transform 0.35s cubic-bezier(0.16,1,0.3,1),box-shadow 0.35s ease;}
        .lp-card-lift:hover{transform:translateY(-6px);box-shadow:0 32px 64px rgba(27,28,25,0.1)!important;}

        .lp-shield{border-top-left-radius:1rem;border-bottom-right-radius:1rem;border-top-right-radius:0.25rem;border-bottom-left-radius:0.25rem;}

        .lp-notice-row{transition:background 0.2s,transform 0.2s;border-radius:10px;}
        .lp-notice-row:hover{background:${C.surfaceContainerLow};transform:translateX(4px);}

        .lp-tag{display:inline-block;padding:2px 10px;border-radius:20px;font-size:10px;font-weight:700;letter-spacing:0.06em;font-family:'Inter',sans-serif;}
        .tag-event{background:#dbeafe;color:#1d4ed8;}.tag-important{background:#fee2e2;color:#dc2626;}
        .tag-holiday{background:#d1fae5;color:#059669;}.tag-admission{background:rgba(202,138,34,0.15);color:${C.tertiaryContainer};}.tag-general{background:${C.surfaceContainerLow};color:${C.onSurfaceVariant};}

        .lp-hamburger{display:none;align-items:center;justify-content:center;width:40px;height:40px;border-radius:10px;cursor:pointer;border:none;background:${C.surfaceContainerLow};color:${C.onSurface};transition:background 0.2s;}
        .lp-hamburger:hover{background:${C.outlineVariant};}

        input:focus,textarea:focus{outline:none;border-color:${C.primary}!important;background:${C.surfaceContainerLowest}!important;}
        *{box-sizing:border-box;}
        html{scroll-behavior:smooth;}
        body{-webkit-text-size-adjust:100%;text-size-adjust:100%;}
        img{max-width:100%;height:auto;}
        .lp-nav-height{height:68px;}
        @media(max-width:480px){.lp-nav-height{height:60px;}}

        @media(max-width:1024px){
          .lp-nav-links{display:none!important;}
          .lp-hamburger{display:inline-flex!important;}
          .lp-hero-cols{grid-template-columns:1fr!important;gap:32px!important;}
          .lp-stats-grid{grid-template-columns:repeat(2,1fr)!important;gap:24px!important;}
          .lp-about-cols{grid-template-columns:1fr!important;gap:40px!important;}
          .lp-values-grid{grid-template-columns:1fr 1fr!important;}
          .lp-bento{grid-template-columns:repeat(2,1fr)!important;}
          .lp-bento-featured{grid-column:span 2!important;}
          .lp-bento-banner{grid-column:span 2!important;}
          .lp-facility-grid{grid-template-columns:1fr 1fr!important;}
          .lp-contact-grid{grid-template-columns:1fr!important;}
          .lp-footer-grid{grid-template-columns:1fr 1fr!important;gap:28px!important;}
        }
        @media(max-width:768px){
          .lp-hero-copy{text-align:center!important;}
          .lp-hero-copy p{margin-left:auto!important;margin-right:auto!important;}
          .lp-hero-ctas{justify-content:center!important;flex-direction:column!important;align-items:center!important;}
          .lp-hero-ctas .lp-btn-primary,.lp-hero-ctas .lp-btn-secondary{width:100%;max-width:320px;justify-content:center!important;}
          .lp-facility-grid{grid-template-columns:1fr!important;}
          .lp-footer-grid{grid-template-columns:1fr!important;}
          .lp-section{padding:64px 16px!important;}
          .lp-hero-info-card{display:none!important;}
          .lp-stats-grid{grid-template-columns:1fr 1fr!important;gap:16px!important;}
          .lp-bento{grid-template-columns:1fr!important;}
          .lp-bento-featured{grid-column:span 1!important;}
          .lp-bento-banner{grid-column:span 1!important;}
          .lp-section-h2{font-size:32px!important;}
          .lp-hero-h1{font-size:36px!important;line-height:1.1!important;}
          .lp-hero-quick-strip{justify-content:center!important;}
          .lp-about-mini-stats{justify-content:center!important;}
          .lp-bento-banner-tiles{gap:8px!important;}
          .lp-bento-banner-tile{width:64px!important;height:64px!important;}
          .lp-footer-grid{grid-template-columns:1fr 1fr!important;gap:24px!important;}
          .lp-footer-bottom{flex-direction:column!important;text-align:center!important;gap:12px!important;}
          .lp-contact-map{height:260px!important;}
          .lp-stat-num{font-size:36px!important;}
        }
        @media(max-width:480px){
          .lp-nav-reg{display:none!important;}
          .lp-nav-login{display:none!important;}
          .lp-hero-h1{font-size:28px!important;line-height:1.12!important;}
          .lp-values-grid{grid-template-columns:1fr 1fr!important;}
          .lp-stats-grid{grid-template-columns:1fr 1fr!important;gap:12px!important;}
          .lp-footer-grid{grid-template-columns:1fr!important;}
          .lp-section{padding:52px 16px!important;}
          .lp-section-h2{font-size:26px!important;}
          .lp-brochure{display:none!important;}
          .lp-stat-num{font-size:32px!important;}
          .lp-bento-banner{padding:20px 16px!important;}
          .lp-bento-banner-tiles{display:none!important;}
          .lp-modal-inner{padding:24px 20px!important;border-radius:16px!important;}
          .lp-nav-height{height:60px!important;}
          .lp-hero-padding{padding-top:80px!important;}
          .lp-notice-date{display:none!important;}
          .lp-footer-brand-text{font-size:13px!important;}
        }
      `}</style>

      {/* ══════════════════════════════════════════════════════════════════
          NAVBAR — glassmorphism
      ══════════════════════════════════════════════════════════════════ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: 'rgba(251,249,244,0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        transition: 'box-shadow 0.4s',
        boxShadow: scrolled ? `0 4px 24px rgba(27,28,25,0.08)` : `0 1px 0 rgba(223,192,178,0.4)`,
        borderBottom: `1px solid rgba(223,192,178,0.3)`,
      }}>
        <div className="lp-nav-height" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px', height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* Logo + School Name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', minWidth: 0, flex: 1 }} onClick={() => scrollTo('hero')}>
            <div style={{ flexShrink: 0 }}>
              <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="LKPS Logo"
                style={{ height: 40, width: 40, borderRadius: '50%', objectFit: 'contain', background: C.surfaceContainerLow, padding: 2 }}
                onError={e => { e.target.style.display = 'none'; }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 14, color: C.secondary, lineHeight: 1.1, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>LORD KRISHNA PUBLIC SCHOOL</div>
              <div style={{ fontSize: 10, fontWeight: 500, color: C.onSurfaceVariant, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2, fontFamily: 'Inter, sans-serif' }}>Ishapur · Mathura</div>
            </div>
          </div>

          {/* Desktop nav links */}
          <div className="lp-nav-links" style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
            {NAV.map(n => (
              <button key={n} onClick={() => scrollTo(n.toLowerCase())} className="lp-nav-link">{n}</button>
            ))}
          </div>

          {/* CTA + Login + hamburger */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
            {/* Divider between nav links and buttons */}
            <div className="lp-nav-links" style={{ width: 1, height: 24, background: 'rgba(223,192,178,0.5)', marginRight: 2 }} />
            <a href="/login" className="lp-nav-login" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, color: C.onSurface, background: C.surfaceContainerLowest, border: `1px solid rgba(223,192,178,0.4)`, textDecoration: 'none', transition: 'all 0.2s', fontFamily: 'Inter, sans-serif', lineHeight: 1 }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(223,192,178,0.4)'; e.currentTarget.style.color = C.onSurface; }}>
              <Icon name="login" size={15} />
              Login
            </a>
            <button className="lp-btn-nav-reg lp-nav-reg" onClick={() => setRegOpen(true)}>
              Register
            </button>
            <button className="lp-hamburger" onClick={() => setMenuOpen(m => !m)}>
              <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div style={{ background: 'rgba(251,249,244,0.97)', backdropFilter: 'blur(12px)', borderTop: `1px solid rgba(223,192,178,0.3)`, padding: '16px 24px 20px' }}>
            {NAV.map(n => (
              <button key={n} onClick={() => scrollTo(n.toLowerCase())}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '12px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600, color: C.onSurface, borderBottom: `1px solid rgba(223,192,178,0.2)`, fontFamily: 'Inter, sans-serif' }}>
                {n}
              </button>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <a href="/login" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px', borderRadius: 10, fontSize: 14, fontWeight: 600, color: C.onSurface, background: C.surfaceContainerLow, border: `1px solid rgba(223,192,178,0.3)`, textDecoration: 'none', fontFamily: 'Inter, sans-serif' }}>
                <Icon name="login" size={16} /> Login
              </a>
              <button className="lp-btn-nav-reg" style={{ flex: 1 }} onClick={() => { setMenuOpen(false); setRegOpen(true); }}>
                Register Now
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* ══════════════════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════════════════ */}
      <section id="hero" ref={heroRef} onMouseMove={onMouseMove}
        style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden', background: C.surface }}>

        {/* Ambient orbs — hidden on mobile via overflow:hidden on section */}
        <div style={{ position: 'absolute', top: '6%', left: '3%', width: 400, height: 400, borderRadius: '50%',
          background: `radial-gradient(circle, rgba(159,66,0,0.07) 0%, transparent 70%)`,
          animation: 'orbFloat1 18s ease-in-out infinite', pointerEvents: 'none',
          transform: `translate(${mouse.x * -14}px, ${mouse.y * -10}px)` }} />
        <div style={{ position: 'absolute', bottom: '4%', right: '4%', width: 360, height: 360, borderRadius: '50%',
          background: `radial-gradient(circle, rgba(76,86,175,0.08) 0%, transparent 70%)`,
          animation: 'orbFloat2 22s ease-in-out infinite', pointerEvents: 'none',
          transform: `translate(${mouse.x * 12}px, ${mouse.y * 8}px)` }} />
        <div style={{ position: 'absolute', top: '40%', right: '20%', width: 240, height: 240, borderRadius: '50%',
          background: `radial-gradient(circle, rgba(202,138,34,0.07) 0%, transparent 70%)`,
          animation: 'orbFloat3 14s ease-in-out infinite', pointerEvents: 'none',
          transform: `translate(${mouse.x * 7}px, ${mouse.y * 5}px)` }} />

        <div className="lp-hero-padding" style={{ position: 'relative', zIndex: 2, maxWidth: 1200, margin: '0 auto', padding: '96px 16px 64px', width: '100%' }}>
          <div className="lp-hero-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>

            {/* Left copy */}
            <div className="lp-hero-copy" style={{ animation: 'fadeUp 0.9s ease both' }}>
              {/* Admissions badge */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C.tertiaryContainer, borderRadius: 999, padding: '6px 16px', marginBottom: 28 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.onPrimary, display: 'inline-block', opacity: 0.9 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: C.onPrimary, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Admissions Open 2026–27</span>
              </div>

              <h1 className="lp-hero-h1" style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 900, fontSize: 58, lineHeight: 1.06, color: C.onSurface, margin: '0 0 16px', letterSpacing: '-0.03em' }}>
                Nurturing <span style={{ color: C.primary, fontStyle: 'italic' }}>Minds</span>,<br />
                Building Futures
              </h1>

              <p style={{ fontSize: 16, color: C.onSurfaceVariant, marginBottom: 8, fontWeight: 400, fontFamily: 'Inter, sans-serif' }}>
                Lord Krishna Public School · Ishapur, Laxminagar, Mathura
              </p>

              <p style={{ fontSize: 16, color: C.onSurface, marginBottom: 36, maxWidth: 480, lineHeight: 1.7, fontWeight: 400, fontFamily: 'Inter, sans-serif' }}>
                Shaping futures with{' '}
                <Typewriter texts={['quality education', 'modern facilities', 'holistic growth', 'strong values']} />
              </p>

              <div className="lp-hero-ctas" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button className="lp-btn-primary" onClick={() => setRegOpen(true)}>
                  <Icon name="how_to_reg" size={20} />
                  Register Now
                </button>
                <button className="lp-btn-secondary" onClick={() => scrollTo('about')}>
                  <Icon name="arrow_forward" size={18} />
                  Explore School
                </button>
              </div>

              {/* Quick info strip */}
              <div className="lp-hero-quick-strip" style={{ display: 'flex', gap: 20, marginTop: 32, flexWrap: 'wrap' }}>
                {[
                  { icon: 'school', text: 'Est. 2009' },
                  { icon: 'groups', text: '300+ Students' },
                  { icon: 'emoji_events', text: '100% Results' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name={item.icon} size={15} style={{ color: C.primary }} />
                    <span style={{ fontSize: 13, color: C.onSurfaceVariant, fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — floating info card */}
            <div className="lp-hero-info-card" style={{ animation: 'fadeUp 1.1s 0.2s ease both', display: 'flex', justifyContent: 'center' }}>
              <div style={{ background: C.surfaceContainerLowest, border: `1px solid rgba(223,192,178,0.3)`, borderRadius: '1rem 0.25rem 1rem 0.25rem', padding: '36px 32px', maxWidth: 380, width: '100%', boxShadow: '0 32px 64px rgba(27,28,25,0.06)', animation: 'floatY 6s ease-in-out infinite' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
                  <div style={{ width: 52, height: 52, borderRadius: '50%', background: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="school" size={28} style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 16, color: C.onSurface }}>Est. 2009</div>
                    <div style={{ fontSize: 12, color: C.onSurfaceVariant, fontFamily: 'Inter, sans-serif' }}>Playway to Class VIII</div>
                  </div>
                </div>
                {[
                  { icon: 'location_on', text: 'Ishapur, Laxminagar, Mathura, UP' },
                  { icon: 'phone',       text: '9997360040 / 8650616990' },
                  { icon: 'schedule',    text: 'Mon – Sat  |  7:00 AM – 2:30 PM' },
                  { icon: 'mail',        text: 'lordkrishnapublicschoolmtr29@gmail.com' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: C.surfaceContainerLow, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name={item.icon} size={16} style={{ color: C.primary }} />
                    </div>
                    <span style={{ fontSize: 12, color: C.onSurfaceVariant, lineHeight: 1.5, paddingTop: 6, fontFamily: 'Inter, sans-serif', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{item.text}</span>
                  </div>
                ))}
                <button className="lp-btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={() => scrollTo('contact')}>
                  <Icon name="directions" size={18} />
                  Get Directions
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, animation: 'floatY 2s ease-in-out infinite' }}>
          <span style={{ fontSize: 10, color: C.outlineVariant, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Scroll</span>
          <Icon name="keyboard_arrow_down" size={22} style={{ color: C.outlineVariant }} />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          STATS BAR — warm amber gradient
      ══════════════════════════════════════════════════════════════════ */}
      <section ref={statsRef} style={{ background: `linear-gradient(135deg, ${C.tertiaryContainer} 0%, ${C.primaryContainer} 100%)`, padding: '48px 16px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.06) 0%, transparent 50%)', pointerEvents: 'none' }} />
        <div className="lp-stats-grid" style={{ maxWidth: 900, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 48, position: 'relative' }}>
          {[
            { value: 15,   suffix: '+', label: 'Years of Excellence' },
            { value: 300,  suffix: '+', label: 'Students Enrolled' },
            { value: 1000, suffix: '+', label: 'Alumni' },
            { value: 100,  suffix: '%', label: 'Annual Results' },
          ].map((s, i) => (
            <div key={i} data-reveal="up" className={`d${i + 1}`}>
              <StatCounter value={s.value} suffix={s.suffix} label={s.label} inView={statsInView} />
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          ABOUT — surface bg, 2-col: text + values grid
      ══════════════════════════════════════════════════════════════════ */}
      <section id="about" className="lp-section" style={{ padding: '96px 24px', background: C.surface }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="lp-about-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>

            {/* Text left */}
            <div data-reveal="right">
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `rgba(159,66,0,0.08)`, borderRadius: 999, padding: '5px 14px', marginBottom: 20 }}>
                <Icon name="info" size={14} style={{ color: C.primary }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: C.primary, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>About Us</span>
              </div>
              <h2 className="lp-section-h2" style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 40, color: C.onSurface, margin: '0 0 20px', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
                Why Choose Lord Krishna<br />Public School?
              </h2>
              <p style={{ fontSize: 16, color: C.onSurfaceVariant, lineHeight: 1.8, marginBottom: 20, fontFamily: 'Inter, sans-serif' }}>
                Lord Krishna Public School has been a beacon of quality education in Mathura since 2009. We combine traditional values with modern pedagogy to create well-rounded individuals ready for tomorrow's challenges.
              </p>
              <p style={{ fontSize: 16, color: C.onSurfaceVariant, lineHeight: 1.8, marginBottom: 32, fontFamily: 'Inter, sans-serif' }}>
                Our dedicated faculty, state-of-the-art facilities, and nurturing environment ensure every child reaches their full potential — academically, socially, and morally.
              </p>
              <div className="lp-about-mini-stats" style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginBottom: 32 }}>
                {[
                  { label: '15+', sub: 'Years Est.' },
                  { label: '300+', sub: 'Students' },
                  { label: '1000+', sub: 'Alumni' },
                ].map((s, i) => (
                  <div key={i}>
                    <div style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 800, fontSize: 24, color: C.primary, lineHeight: 1 }}>{s.label}</div>
                    <div style={{ fontSize: 12, color: C.onSurfaceVariant, marginTop: 4, fontFamily: 'Inter, sans-serif' }}>{s.sub}</div>
                  </div>
                ))}
              </div>
              <button className="lp-btn-primary" onClick={() => setRegOpen(true)}>
                <Icon name="how_to_reg" size={18} />
                Enroll Your Child
              </button>
            </div>

            {/* Values grid right — 2×2 */}
            <div className="lp-values-grid" data-reveal="left" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {VALUES.map((v, i) => (
                <div key={i} className="lp-card-lift" style={{ background: C.surfaceContainerLowest, borderRadius: '1rem 0.25rem 1rem 0.25rem', padding: '24px 20px', boxShadow: '0 2px 24px rgba(27,28,25,0.06)', border: `1px solid rgba(223,192,178,0.3)` }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: `${v.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    <Icon name={v.icon} size={22} style={{ color: v.color }} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.onSurface, marginBottom: 6, fontFamily: 'Lexend, sans-serif' }}>{v.label}</div>
                  <div style={{ fontSize: 12, color: C.onSurfaceVariant, lineHeight: 1.6, fontFamily: 'Inter, sans-serif' }}>{v.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          ACADEMICS — bento grid, surface container low bg
      ══════════════════════════════════════════════════════════════════ */}
      <section id="academics" className="lp-section" style={{ padding: '96px 24px', background: C.surfaceContainerLow }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 48, flexWrap: 'wrap', gap: 16 }} data-reveal="up">
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `rgba(76,86,175,0.1)`, borderRadius: 999, padding: '5px 14px', marginBottom: 16 }}>
                <Icon name="menu_book" size={14} style={{ color: C.secondary }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: C.secondary, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Academics</span>
              </div>
              <h2 className="lp-section-h2" style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 40, color: C.onSurface, margin: 0, letterSpacing: '-0.02em' }}>
                Tailored Academic Journeys
              </h2>
              <p style={{ fontSize: 16, color: C.onSurfaceVariant, marginTop: 12, maxWidth: 520, fontFamily: 'Inter, sans-serif' }}>
                Curriculum designed to foster curiosity at every stage of a child's development.
              </p>
            </div>
          </div>

          {/* Bento grid */}
          <div className="lp-bento" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>

            {/* Playway — 1 col */}
            <div data-reveal="up" className="lp-card-lift d1" style={{ background: C.surfaceContainerLowest, borderRadius: '1rem 0.25rem 1rem 0.25rem', padding: '32px 24px', boxShadow: '0 2px 24px rgba(27,28,25,0.06)', border: `1px solid rgba(223,192,178,0.3)` }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `rgba(202,138,34,0.12)`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                <Icon name="child_care" size={28} style={{ color: C.tertiaryContainer }} />
              </div>
              <h3 style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 20, color: C.onSurface, margin: '0 0 10px' }}>Playway & KG</h3>
              <p style={{ fontSize: 13, color: C.onSurfaceVariant, lineHeight: 1.7, margin: '0 0 20px', fontFamily: 'Inter, sans-serif' }}>
                Play-based early learning with phonics, numeracy, and creative arts.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['Sensory Play', 'Motor Skills', 'Phonics'].map(item => (
                  <li key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: C.onSurfaceVariant, fontFamily: 'Inter, sans-serif' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.tertiaryContainer, flexShrink: 0 }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Primary Wing — 2 col featured navy card */}
            <div data-reveal="up" className="lp-bento-featured d2" style={{ gridColumn: 'span 2', background: C.secondary, borderRadius: '1rem 0.25rem 1rem 0.25rem', padding: '36px 32px', position: 'relative', overflow: 'hidden', boxShadow: '0 32px 64px rgba(76,86,175,0.2)' }}>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <h3 style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 28, color: '#ffffff', margin: '0 0 12px' }}>Primary Wing</h3>
                <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', maxWidth: 340, marginBottom: 32, lineHeight: 1.7, fontFamily: 'Inter, sans-serif' }}>
                  Strong foundation in Maths, Science, English, Hindi, and EVS through inquiry-based learning.
                </p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {['Grade I – V', 'Core Subjects', 'Project Learning'].map(tag => (
                    <span key={tag} style={{ padding: '6px 14px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', borderRadius: 8, fontSize: 12, color: '#ffffff', fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>{tag}</span>
                  ))}
                </div>
              </div>
              <span className="material-symbols-outlined" style={{ position: 'absolute', bottom: -20, right: -20, fontSize: 180, color: 'rgba(255,255,255,0.07)', userSelect: 'none', transform: 'rotate(-12deg)', transition: 'transform 0.5s' }}>menu_book</span>
            </div>

            {/* Middle School — 1 col */}
            <div data-reveal="up" className="lp-card-lift d3" style={{ background: C.surfaceContainerLowest, borderRadius: '1rem 0.25rem 1rem 0.25rem', padding: '32px 24px', boxShadow: '0 2px 24px rgba(27,28,25,0.06)', border: `1px solid rgba(223,192,178,0.3)` }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `rgba(76,86,175,0.1)`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                <Icon name="psychology" size={28} style={{ color: C.secondary }} />
              </div>
              <h3 style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 20, color: C.onSurface, margin: '0 0 10px' }}>Middle School</h3>
              <p style={{ fontSize: 13, color: C.onSurfaceVariant, lineHeight: 1.7, margin: '0 0 20px', fontFamily: 'Inter, sans-serif' }}>
                Conceptual depth in all subjects with project-based learning for VI–VIII.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {['STEM Focus', 'Debating', 'Research Skills'].map(item => (
                  <li key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13, color: C.onSurfaceVariant, fontFamily: 'Inter, sans-serif' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.secondary, flexShrink: 0 }} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Co-Curricular — 4 col wide banner */}
            <div data-reveal="up" className="lp-bento-banner d4" style={{ gridColumn: 'span 4', background: C.primaryContainer, borderRadius: '1rem 0.25rem 1rem 0.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 28px', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <h3 style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 20, color: C.onPrimary, margin: '0 0 6px' }}>Beyond the Classroom</h3>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', margin: 0, fontFamily: 'Inter, sans-serif' }}>
                  Holistic development through sports, arts, music, and cultural activities.
                </p>
              </div>
              <div className="lp-bento-banner-tiles" style={{ display: 'flex', gap: 10 }}>
                {[
                  { icon: 'palette',       label: 'Arts' },
                  { icon: 'sports_soccer', label: 'Sports' },
                  { icon: 'music_note',    label: 'Music' },
                  { icon: 'theater_comedy',label: 'Culture' },
                ].map(item => (
                  <div key={item.label} className="lp-bento-banner-tile" style={{ width: 72, height: 72, borderRadius: 12, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                    <Icon name={item.icon} size={22} style={{ color: '#ffffff' }} />
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#ffffff', fontFamily: 'Inter, sans-serif' }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          FACILITIES — surface bg, asymmetric-shield image containers
      ══════════════════════════════════════════════════════════════════ */}
      <section id="facilities" className="lp-section" style={{ padding: '96px 24px', background: C.surface }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }} data-reveal="up">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `rgba(131,85,0,0.1)`, borderRadius: 999, padding: '5px 14px', marginBottom: 16 }}>
              <Icon name="apartment" size={14} style={{ color: C.tertiary }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: C.tertiary, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Facilities</span>
            </div>
            <h2 className="lp-section-h2" style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 40, color: C.onSurface, margin: '0 0 12px', letterSpacing: '-0.02em' }}>
              Infrastructure of Tomorrow
            </h2>
            <p style={{ fontSize: 16, color: C.onSurfaceVariant, maxWidth: 520, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
              Modern facilities that create the perfect environment for learning, creativity, and physical development.
            </p>
          </div>

          <div className="lp-facility-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 24 }}>
            {FACILITIES.map((f, i) => (
              <FacilityCard key={i} facility={f} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          NOTICES — surface container low bg
      ══════════════════════════════════════════════════════════════════ */}
      <section id="notices" className="lp-section" style={{ padding: '96px 24px', background: C.surfaceContainerLow }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }} data-reveal="up">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `rgba(186,26,26,0.08)`, borderRadius: 999, padding: '5px 14px', marginBottom: 16 }}>
              <Icon name="campaign" size={14} style={{ color: '#ba1a1a' }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#ba1a1a', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Notices</span>
            </div>
            <h2 className="lp-section-h2" style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 40, color: C.onSurface, margin: '0 0 12px', letterSpacing: '-0.02em' }}>
              Latest Announcements
            </h2>
            <p style={{ fontSize: 16, color: C.onSurfaceVariant, fontFamily: 'Inter, sans-serif' }}>Stay updated with school news, events, and important notices.</p>
          </div>

          <div data-reveal="up" style={{ background: C.surfaceContainerLowest, borderRadius: '1rem 0.25rem 1rem 0.25rem', boxShadow: '0 32px 64px rgba(27,28,25,0.06)', overflow: 'hidden', border: `1px solid rgba(223,192,178,0.3)` }}>
            {notices.length === 0 ? (
              <div style={{ padding: '56px 32px', textAlign: 'center' }}>
                <Icon name="notifications_none" size={48} style={{ color: C.outlineVariant, display: 'block', margin: '0 auto 12px' }} />
                <p style={{ color: C.onSurfaceVariant, fontSize: 15, fontFamily: 'Inter, sans-serif' }}>No notices at the moment. Check back soon.</p>
              </div>
            ) : (
              notices.map((n, i) => (
                <div key={n._id || i} className="lp-notice-row"
                  style={{ padding: '18px 24px', borderBottom: i < notices.length - 1 ? `1px solid ${C.surfaceContainerLow}` : 'none', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: C.surfaceContainerLow, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <Icon name="article" size={18} style={{ color: C.primary }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 14, color: C.onSurface, fontFamily: 'Lexend, sans-serif' }}>{n.title}</span>
                      {n.category && <span className={tagClass(n.category)}>{n.category}</span>}
                    </div>
                    {n.body && <p style={{ fontSize: 13, color: C.onSurfaceVariant, margin: 0, lineHeight: 1.6, fontFamily: 'Inter, sans-serif' }}>{n.body}</p>}
                  </div>
                  {n.date && (
                    <span className="lp-notice-date" style={{ fontSize: 11, color: C.outlineVariant, whiteSpace: 'nowrap', flexShrink: 0, paddingTop: 2, fontFamily: 'Inter, sans-serif' }}>
                      {new Date(n.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          CONTACT — surface bg, Google Maps + 4 info cards
      ══════════════════════════════════════════════════════════════════ */}
      <section id="contact" className="lp-section" style={{ padding: '96px 24px', background: C.surface }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }} data-reveal="up">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: `rgba(76,86,175,0.1)`, borderRadius: 999, padding: '5px 14px', marginBottom: 16 }}>
              <Icon name="contact_mail" size={14} style={{ color: C.secondary }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: C.secondary, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Contact Us</span>
            </div>
            <h2 className="lp-section-h2" style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 40, color: C.onSurface, margin: '0 0 12px', letterSpacing: '-0.02em' }}>
              Find Us &amp; Get in Touch
            </h2>
            <p style={{ fontSize: 16, color: C.onSurfaceVariant, maxWidth: 480, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
              We'd love to hear from you. Visit us or reach out through any of the channels below.
            </p>
          </div>

          <div className="lp-contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'start' }}>
            {/* Map */}
            <div data-reveal="right" style={{ borderRadius: '1rem 0.25rem 1rem 0.25rem', overflow: 'hidden', boxShadow: '0 32px 64px rgba(27,28,25,0.06)', border: `1px solid rgba(223,192,178,0.3)` }}>
              <iframe
                title="Lord Krishna Public School Location"
                src="https://maps.google.com/maps?q=Lord+Krishna+Public+School+Ishapur+Laxminagar+Mathura+Uttar+Pradesh&t=&z=16&ie=UTF8&iwloc=&output=embed"
                width="100%" className="lp-contact-map" style={{ height: 360, border: 0, display: 'block' }}
                allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>

            {/* Info cards */}
            <div data-reveal="left" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { icon: 'location_on', color: C.secondary,         bg: `rgba(76,86,175,0.08)`,  title: 'Address',      lines: ['Ishapur, Laxminagar', 'Mathura, Uttar Pradesh'] },
                { icon: 'phone',       color: C.primary,           bg: `rgba(159,66,0,0.08)`,   title: 'Phone',        lines: ['9997360040', '8650616990'] },
                { icon: 'mail',        color: C.tertiary,          bg: `rgba(131,85,0,0.08)`,   title: 'Email',        lines: ['lordkrishnapublicschoolmtr29@gmail.com'] },
                { icon: 'schedule',    color: C.tertiaryContainer, bg: `rgba(202,138,34,0.1)`,  title: 'School Hours', lines: ['Monday – Saturday', '7:00 AM – 2:30 PM'] },
              ].map((c, i) => (
                <div key={i} className="lp-card-lift" style={{ background: C.surfaceContainerLowest, borderRadius: '0.75rem 0.25rem 0.75rem 0.25rem', padding: '18px 22px', display: 'flex', alignItems: 'flex-start', gap: 16, boxShadow: '0 2px 24px rgba(27,28,25,0.06)', border: `1px solid rgba(223,192,178,0.3)` }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name={c.icon} size={20} style={{ color: c.color }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: C.onSurface, marginBottom: 4, fontFamily: 'Lexend, sans-serif' }}>{c.title}</div>
                    {c.lines.map((l, j) => <div key={j} style={{ fontSize: 13, color: C.onSurfaceVariant, lineHeight: 1.6, fontFamily: 'Inter, sans-serif', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{l}</div>)}
                  </div>
                </div>
              ))}

              <button className="lp-btn-primary" style={{ marginTop: 8, justifyContent: 'center' }} onClick={() => setRegOpen(true)}>
                <Icon name="how_to_reg" size={18} />
                Apply for Admission
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          FOOTER — dark navy #0f172a, 4-col grid
      ══════════════════════════════════════════════════════════════════ */}
      <footer style={{ background: '#0f172a', color: 'rgba(255,255,255,0.5)', padding: '56px 16px 28px', borderTopLeftRadius: 24, borderTopRightRadius: 24 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="lp-footer-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 48, marginBottom: 56 }}>

            {/* Brand */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
                <img src="/assets/logo.png" alt="LKPS" style={{ height: 40, width: 40, borderRadius: '50%', objectFit: 'cover', border: `2px solid rgba(202,138,34,0.4)` }}
                  onError={e => { e.target.style.display = 'none'; }} />
                <div>
                  <div style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 14, color: '#ffffff' }}>Lord Krishna Public School</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif' }}>Ishapur, Mathura</div>
                </div>
              </div>
              <p style={{ fontSize: 13, lineHeight: 1.8, maxWidth: 280, marginBottom: 20, fontFamily: 'Inter, sans-serif' }}>
                Empowering young minds with quality education, strong values, and modern facilities since 2009.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  { icon: 'thumb_up',     label: 'Facebook' },
                  { icon: 'photo_camera', label: 'Instagram' },
                  { icon: 'smart_display',label: 'YouTube' },
                ].map(s => (
                  <div key={s.label} title={s.label} style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'background 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(202,138,34,0.2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}>
                    <Icon name={s.icon} size={16} style={{ color: 'rgba(255,255,255,0.55)' }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Quick links */}
            <div>
              <div style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 12, color: '#ffffff', marginBottom: 18, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Quick Links</div>
              {NAV.map(n => (
                <button key={n} onClick={() => scrollTo(n.toLowerCase())}
                  style={{ display: 'block', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'rgba(255,255,255,0.5)', padding: '5px 0', fontFamily: 'Inter, sans-serif', transition: 'color 0.2s', textAlign: 'left' }}
                  onMouseEnter={e => e.target.style.color = C.tertiaryContainer}
                  onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.5)'}>
                  {n}
                </button>
              ))}
            </div>

            {/* Programs */}
            <div>
              <div style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 12, color: '#ffffff', marginBottom: 18, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Programs</div>
              {['Playway & KG', 'Primary (I–V)', 'Middle (VI–VIII)', 'Co-Curricular', 'Sports'].map(p => (
                <div key={p} style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', padding: '5px 0', fontFamily: 'Inter, sans-serif' }}>{p}</div>
              ))}
            </div>

            {/* Contact */}
            <div>
              <div style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 12, color: '#ffffff', marginBottom: 18, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Contact</div>
              {[
                { icon: 'location_on', text: 'Ishapur, Laxminagar, Mathura, UP' },
                { icon: 'phone',       text: '9997360040 / 8650616990' },
                { icon: 'mail',        text: 'lordkrishnapublicschoolmtr29@gmail.com' },
                { icon: 'schedule',    text: 'Mon–Sat, 7:00 AM – 2:30 PM' },
              ].map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12 }}>
                  <Icon name={c.icon} size={14} style={{ color: C.tertiaryContainer, marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, fontFamily: 'Inter, sans-serif' }}>{c.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="lp-footer-bottom" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <span className="lp-footer-brand-text" style={{ fontSize: 12, fontFamily: 'Inter, sans-serif' }}>© {new Date().getFullYear()} Lord Krishna Public School, Mathura. All rights reserved.</span>
            <button className="lp-btn-nav-reg" onClick={() => setRegOpen(true)}>Admissions Open 2026–27</button>
          </div>
        </div>
      </footer>

      {/* ══════════════════════════════════════════════════════════════════
          REGISTRATION MODAL — glassmorphism overlay
      ══════════════════════════════════════════════════════════════════ */}
      {regOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={e => { if (e.target === e.currentTarget) closeReg(); }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(27,28,25,0.55)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }} />
          <div className="lp-modal-inner" style={{ position: 'relative', background: C.surfaceContainerLowest, borderRadius: '1rem 0.25rem 1rem 0.25rem', padding: '36px 32px', maxWidth: 460, width: '100%', boxShadow: '0 32px 80px rgba(27,28,25,0.2)', animation: 'fadeUp 0.35s ease both', border: `1px solid rgba(223,192,178,0.3)` }}>
            <button onClick={closeReg} style={{ position: 'absolute', top: 16, right: 16, background: C.surfaceContainerLow, border: 'none', borderRadius: 10, width: 36, height: 36, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="close" size={20} style={{ color: C.onSurfaceVariant }} />
            </button>

            {regDone ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <Icon name="check_circle" size={40} style={{ color: '#059669' }} />
                </div>
                <h3 style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 24, color: C.onSurface, margin: '0 0 10px' }}>Registration Received!</h3>
                <p style={{ color: C.onSurfaceVariant, fontSize: 15, lineHeight: 1.7, marginBottom: 28, fontFamily: 'Inter, sans-serif' }}>
                  Thank you! Our admissions team will contact you within 24 hours.
                </p>
                <button className="lp-btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={closeReg}>Done</button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: C.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name="how_to_reg" size={24} style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h3 style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 20, color: C.onSurface, margin: 0 }}>Admission Enquiry</h3>
                    <p style={{ fontSize: 12, color: C.onSurfaceVariant, margin: 0, fontFamily: 'Inter, sans-serif' }}>Session 2026–27 · Lord Krishna Public School</p>
                  </div>
                </div>

                <form onSubmit={handleReg}>
                  {[
                    { label: "Student's Full Name *", key: 'name',  type: 'text',  placeholder: 'Enter student name' },
                    { label: 'Parent Phone Number *', key: 'phone', type: 'tel',   placeholder: '10-digit mobile number' },
                    { label: 'Email Address (optional)', key: 'email', type: 'email', placeholder: 'parent@email.com' },
                  ].map(f => (
                    <div key={f.key} style={{ marginBottom: 18 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: C.onSurface, marginBottom: 6, letterSpacing: '0.03em', fontFamily: 'Inter, sans-serif' }}>{f.label}</label>
                      <input type={f.type} placeholder={f.placeholder} value={regForm[f.key]}
                        onChange={e => setRegForm(p => ({ ...p, [f.key]: e.target.value }))}
                        style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${C.outlineVariant}`, fontSize: 14, fontFamily: 'Inter, sans-serif', color: C.onSurface, background: C.surfaceContainerLow, transition: 'border-color 0.2s, background 0.2s' }} />
                    </div>
                  ))}

                  {regError && (
                    <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Icon name="error" size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: '#dc2626', fontFamily: 'Inter, sans-serif' }}>{regError}</span>
                    </div>
                  )}

                  <button type="submit" className="lp-btn-primary" disabled={regLoading}
                    style={{ width: '100%', justifyContent: 'center', opacity: regLoading ? 0.7 : 1 }}>
                    {regLoading
                      ? <><Icon name="hourglass_empty" size={18} />Submitting…</>
                      : <><Icon name="send" size={18} />Submit Enquiry</>}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          BROCHURE POPUP — bottom-right after 5s
      ══════════════════════════════════════════════════════════════════ */}
      {brochureOpen && (
        <div className="lp-brochure" style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1500, animation: 'fadeUp 0.5s ease both', width: 280 }}>
          <div style={{ background: C.surfaceContainerLowest, borderRadius: '1rem 0.25rem 1rem 0.25rem', boxShadow: '0 32px 64px rgba(27,28,25,0.12)', overflow: 'hidden', border: `1px solid rgba(223,192,178,0.3)`, position: 'relative' }}>
            <button onClick={() => setBrochureOpen(false)}
              style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(27,28,25,0.45)', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
              <Icon name="close" size={16} style={{ color: '#fff' }} />
            </button>
            <div style={{ background: `linear-gradient(135deg, ${C.primary}, ${C.primaryContainer})`, padding: '28px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="school" size={56} style={{ color: 'rgba(255,255,255,0.9)' }} />
            </div>
            <div style={{ padding: '18px 20px 20px' }}>
              <div style={{ fontFamily: 'Lexend, sans-serif', fontWeight: 700, fontSize: 14, color: C.onSurface, marginBottom: 6 }}>
                Admissions Open 2026–27
              </div>
              <p style={{ fontSize: 12, color: C.onSurfaceVariant, margin: '0 0 14px', lineHeight: 1.6, fontFamily: 'Inter, sans-serif' }}>
                Limited seats available. Register today to secure your child's future.
              </p>
              <button className="lp-btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '10px 20px', fontSize: 13 }}
                onClick={() => { setBrochureOpen(false); setRegOpen(true); }}>
                <Icon name="how_to_reg" size={16} />
                Register Now
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
